import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { FiMapPin, FiTruck, FiBox } from "react-icons/fi";
import "leaflet/dist/leaflet.css";

const GOOGLE_API_KEY = "AIzaSyD16h98hJe26HkkEvzKPbH2WytgT5srggM";

// Controller aggiornato per un movimento fluido
const MapController = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (coords) {
      map.flyTo(coords, 6, { animate: true, duration: 2 });
    }
  }, [coords, map]);
  return null;
};

// Icone custom in stile "Glow"
const createIcon = (color, isGlow = false) => {
  return new L.DivIcon({
    html: `<div style="
            width: 12px; height: 12px; 
            background-color: ${color}; 
            border: 2px solid white; 
            border-radius: 50%; 
            box-shadow: ${isGlow ? `0 0 15px ${color}` : 'none'};
          "></div>`,
    className: "",
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
};

const iconOrigin = createIcon("#8d8d8d"); // Grigio per fornitore
const iconDest = createIcon("#b81f25", true); // Rosso glow per cliente

const MappaCrossDocking = () => {
  const [percorsi, setPercorsi] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const getCoords = async (item, tipo) => {
    const prov = item[`PROVINCIA ${tipo}`];
    const naz = item[`NAZIONE ${tipo}`];
    const query = prov ? `${prov}, ${naz}` : naz;
    if (!query) return [45.0, 12.0];
    const cache = JSON.parse(localStorage.getItem("geocodeCache") || "{}");
    if (cache[query]) return cache[query];
    try {
      const res = await axios.get("https://maps.googleapis.com/maps/api/geocode/json", {
        params: { address: query, key: GOOGLE_API_KEY },
      });
      if (res.data.status === "OK") {
        const loc = res.data.results[0].geometry.location;
        const coords = [loc.lat, loc.lng];
        cache[query] = coords;
        localStorage.setItem("geocodeCache", JSON.stringify(cache));
        return coords;
      }
    } catch (e) { return [45.0, 12.0]; }
    return [45.0, 12.0];
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`ordini_urgenti.json?nocache=${new Date().getTime()}`);
        const data = await res.json();
        let listaRaw = Array.isArray(data.CROSS_DOCKING) ? data.CROSS_DOCKING : (data.CROSS_DOCKING?.[0] || []);
        
        const validi = listaRaw.filter(item => item && item.Is_Totale === "0");
        const elaborati = [];
        for (let i = 0; i < validi.length; i++) {
          const item = validi[i];
          const start = await getCoords(item, "FORNITORE");
          const end = await getCoords(item, "CLIENTE");
          elaborati.push({ ...item, id_u: `${item.Doc_Cliente}-${i}`, start, end });
          setPercorsi([...elaborati]);
        }
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const selectedPath = useMemo(() => percorsi.find((p) => p.id_u === selectedId), [selectedId, percorsi]);

  return (
    <div className="shadow-lg card border-0" style={{ 
      borderRadius: "16px", maxHeight: "350px", display: "flex", 
      background: "#111", overflow: "hidden", border: "1px solid #333" 
    }}>
      <style>{`
        .sidebar-list::-webkit-scrollbar { width: 4px; }
        .sidebar-list::-webkit-scrollbar-thumb { background: #b81f25; border-radius: 10px; }
        .path-card { 
          transition: all 0.2s; border: 1px solid #222; background: #1a1a1a;
          position: relative; overflow: hidden;
        }
        .path-card.active { background: #251212; border-color: #b81f25; }
        .path-card:hover { transform: translateX(5px); background: #222; }
        
        @keyframes dash {
          to { stroke-dashoffset: 0; }
        }
        .animated-polyline {
          stroke-dasharray: 10;
          stroke-dashoffset: 100;
          animation: dash 5s linear infinite;
        }
      `}</style>

      {/* SIDEBAR TECNICA */}
      <div className="sidebar-list" style={{ width: "320px", overflowY: "auto", borderRight: "1px solid #222", padding: "20px" }}>
        <div className="mb-4 d-flex align-items-center">
            <FiMapPin className="text-danger me-2" />
            <span style={{ fontWeight: "800", fontSize: "0.7rem", color: "#666", letterSpacing: "1.5px" }}>TRACKING ROTTE</span>
        </div>
        
        {loading && <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-danger"></div></div>}
        
        {percorsi.map((p) => (
          <div
            key={p.id_u}
            onClick={() => setSelectedId(p.id_u)}
            className={`path-card p-3 mb-2 rounded-3 cursor-pointer ${selectedId === p.id_u ? 'active' : ''}`}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex justify-content-between align-items-start">
                <div className="text-white fw-bold mb-1" style={{ fontSize: '0.8rem' }}>{p.Cliente}</div>
                <FiTruck size={14} className={selectedId === p.id_u ? 'text-danger' : 'text-muted'} />
            </div>
            <div className="text-muted d-flex align-items-center" style={{ fontSize: "0.65rem" }}>
              <span className="text-uppercase">Da: {p.Fornitore}</span>
            </div>
          </div>
        ))}
      </div>

      {/* MAPPA DARK MODE */}
      <div style={{ flex: 1, position: "relative" }}>
        <MapContainer
          center={[45.0, 12.0]}
          zoom={5}
          zoomControl={false}
          style={{ height: "100%", width: "100%", background: "#0d0d0d" }}
        >
          <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
          
          {percorsi.map((p) => (
            <React.Fragment key={p.id_u}>
              {/* Mostriamo i marker solo se selezionati o se vogliamo vedere tutto */}
              {(selectedId === p.id_u || !selectedId) && (
                <>
                  <Marker position={p.start} icon={iconOrigin} />
                  <Marker position={p.end} icon={iconDest}>
                    <Popup className="dark-popup">
                        <div style={{ background: '#111', color: '#fff', padding: '5px' }}>
                            <strong className="text-danger">{p.Cliente}</strong><br/>
                            <small>Origine: {p.Fornitore}</small>
                        </div>
                    </Popup>
                  </Marker>
                  <Polyline
                    positions={[p.start, p.end]}
                    className="animated-polyline"
                    pathOptions={{
                      color: selectedId === p.id_u ? "#b81f25" : "#444",
                      weight: selectedId === p.id_u ? 4 : 1,
                      opacity: selectedId === p.id_u ? 1 : 0.3,
                    }}
                  />
                </>
              )}
            </React.Fragment>
          ))}
          {selectedPath && <MapController coords={selectedPath.end} />}
        </MapContainer>

        {/* Overlay Info */}
        {selectedPath && (
            <div style={{
                position: 'absolute', bottom: '20px', left: '20px', zIndex: 1000,
                background: 'rgba(20,20,20,0.9)', padding: '15px', borderRadius: '12px',
                border: '1px solid #b81f25', backdropFilter: 'blur(10px)', minWidth: '200px'
            }}>
                <div className="text-danger fw-bold" style={{ fontSize: '0.7rem' }}>DETTAGLIO TRATTA</div>
                <div className="text-white fw-bold small uppercase">{selectedPath.Fornitore} <FiArrowRight className="mx-1"/> {selectedPath.Cliente}</div>
                <div className="text-muted mt-1" style={{ fontSize: '0.6rem' }}>ORDINE: {selectedPath.Doc_Cliente}</div>
            </div>
        )}
      </div>
    </div>
  );
};

// Icona freccia mancante nell'import
const FiArrowRight = ({className}) => (
    <svg className={className} stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" height="1em" width="1em"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
);

export default MappaCrossDocking;